/**
 * Conversation History Component
 * 
 * This component displays WhatsApp conversation history
 * with questions and answers from all sessions.
 */

import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';
import { whatsappAPI } from '../../api';

const ConversationHistory = ({ selectedSessionId = null, selectedPhoneNumber = null }) => {
  const { state } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'user', 'bot'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConversations();
  }, [selectedSessionId, selectedPhoneNumber]);

  const fetchConversations = async () => {
    if (!state.token) return;
    
    setLoading(true);
    try {
      let data;
      if (selectedSessionId) {
        data = await whatsappAPI.getSessionConversations(state.token, selectedSessionId);
      } else {
        data = await whatsappAPI.getConversations(
          state.token, 
          selectedSessionId, 
          selectedPhoneNumber, 
          500
        );
      }
      setConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(
      state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US'
    );
  };

  const getStepName = (step) => {
    const stepNames = {
      welcome: 'مرحباً',
      personal_info_name: 'معلومات شخصية - الاسم',
      personal_info_id: 'معلومات شخصية - الهوية',
      personal_info_address: 'معلومات شخصية - العنوان',
      storage_items: 'تفاصيل التخزين - المحتويات',
      storage_space: 'تفاصيل التخزين - المساحة',
      storage_duration: 'تفاصيل التخزين - المدة',
      additional_services: 'الخدمات الإضافية',
      images: 'الصور',
      confirmation: 'التأكيد',
      completed: 'مكتمل',
      ended: 'منتهية'
    };
    return stepNames[step] || step;
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesFilter = filter === 'all' || conv.message_type === filter;
    const matchesSearch = !searchTerm || 
      conv.message_content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.phone_number.includes(searchTerm) ||
      getStepName(conv.step).toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const groupConversationsBySession = (conversations) => {
    const grouped = {};
    conversations.forEach(conv => {
      if (!grouped[conv.session_id]) {
        grouped[conv.session_id] = {
          session_id: conv.session_id,
          phone_number: conv.phone_number,
          messages: []
        };
      }
      grouped[conv.session_id].messages.push(conv);
    });
    
    return Object.values(grouped).map(group => ({
      ...group,
      messages: group.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    }));
  };

  const groupedConversations = groupConversationsBySession(filteredConversations);

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className="conversation-history">
      <div className="conversation-header">
        <h3>{t.conversationLog}</h3>
        <div className="conversation-filters">
          <div className="filter-group">
            <label>{t.messageType}:</label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">{t.all}</option>
              <option value="user">{t.user}</option>
              <option value="bot">{t.bot}</option>
            </select>
          </div>
          <div className="search-group">
            <input
              type="text"
              placeholder={t.searchInConversations}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      <div className="conversations-list">
        {groupedConversations.length === 0 ? (
          <div className="no-conversations">
            {t.noConversationsAvailable}
          </div>
        ) : (
          groupedConversations.map(session => (
            <div key={session.session_id} className="session-group">
              <div className="session-header">
                <div className="session-info">
                  <span className="phone-number">{session.phone_number}</span>
                  <span className="session-id">ID: {session.session_id}</span>
                </div>
                <div className="message-count">
                  {session.messages.length} {t.message}
                </div>
              </div>
              
              <div className="messages-container">
                {session.messages.map((message, index) => (
                  <div 
                    key={`${message._id}-${index}`} 
                    className={`message ${message.message_type}`}
                  >
                    <div className="message-header">
                      <span className="message-type">
                        {message.message_type === 'user' ? `👤 ${t.theUser}` : `🤖 ${t.theBot}`}
                      </span>
                      <span className="message-step">
                        {getStepName(message.step)}
                      </span>
                      <span className="message-time">
                        {formatDate(message.timestamp)}
                      </span>
                    </div>
                    <div className="message-content">
                      {message.message_content}
                    </div>
                    {message.media_data && (
                      <div className="message-media">
                        <span className="media-indicator">📎 {t.attachment}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationHistory;
